<template>
	<div id="quarterly-holdings" :class="$store.state.audience">
		<page-banner :widget-data="pageBannerWidgetData" />
		<div class="holding-container">
			<div id="holding-results">
				<div class="header">
					<h2 class="module-header">
						All Holdings
						<disclosure-superscript anchor="quarterlyHoldings" class="quarterly-holdings-page"
							:statistics="['TTLHLD']" />
					</h2>
					<utility-link :link-url="detailPageLink" :link-label="detailPageLinkText" :type="'arrowRight'" />
				</div>
				<div class="holdings-filters">
					<span class="total-holdings">{{ totalHoldings }} Total Holdings:</span>
					<div v-if="breakpoint.isLarge" class="inline">
						<ul class="holdings-filters-list">
							<li v-for="(type) in types" :key="type">
								<a :class="[selectedType === type ? 'active' : '']" @click="changeSelectedType(type)">{{
									type }}</a>
							</li>
						</ul>
					</div>
					<div v-if="breakpoint.isMedium || breakpoint.isSmall" id="filter-dropdown">
						<select v-model="selectedType" class="tab-select">
							<option v-for="(type) in types" :key="type">{{ type }}</option>
						</select>
					</div>
					<div class="as-of-date">As of {{ asOfDate }}</div>
				</div>
				<holdings-table :holdings-data="quarterlyHoldingsData" :holdings-meta-data="quarterlyHoldingsMetaData"
					:first-row="firstRow" :number-of-results-requested="numberOfResultsRequested" :default-sort="sortBy"
					:selectedfilter="selectedType" :staging-flag="$store.state.stagingFlag"
					@options-update="updateTable" />
				<div class="flex-container shim-top-medium">
					<utility-link :link-url="'#download-modal'" :link-label="'Download Fund Holdings'"
						:type="'download'" />
				</div>
			</div>
		</div>
	</div>
</template>
<script>
import store from '_AFIS_/store/store';
import QuarterlyHoldingsTable from '../QuarterlyHoldingsComponents/QuarterlyHoldingsTable';
import PageBanner from '../Shared/PageBanner.vue';
import { mapGetters } from 'vuex';

export default {
	name: 'QuarterlyHoldings',
	components: {
		'holdings-table': QuarterlyHoldingsTable,
		'page-banner': PageBanner
	},
	data() {
		this.types = [
			'All',
			'Equity',
			'Fixed-Income',
			'Cash & Equivalents',
			'Investment Fund'
		];
		this.selectedType = 'All';
		return {
			firstRow: 0,	//on page load the first row is 0, this is updated and used in the params when navigating the table
			sortBy: 'pctNetAssets',
			numberOfResultsRequested: 15, //this is the default set in the api. Can be passed as an input to the api call to get back more than 15 per page. Calculations would need to be updated as well if you change this
			filterKey: 'All',
			title: 'QuarterlyHoldingsPage',
			detailPageLinkText: `back to fund's detail page`,
			selectedType: this.selectedType
		};
	},
	computed: {
		...mapGetters([
			'getQuarterlyHoldingsData',
			'getQuarterlyHoldingsMetaData',
			'getFundAttributes',
			'getFundInfoData',
			'getAudience',
			'getBaseUrl'
		]),
		asOfDate() {
			return (
				(this.quarterlyHoldingsMetaData && this.quarterlyHoldingsMetaData.minAsOfDate) ||
				''
			);
		},
		totalHoldings() {
			return (
				(this.quarterlyHoldingsMetaData &&
					this.quarterlyHoldingsMetaData.allHoldingsCount) ||
				''
			);
		},
		pageBannerWidgetData() {
			return {
				audience: this.getAudience,
				title: this.fundNameSup,
				link: this.investmentsLink,
				fundGroupDescription: this.fundGroupDescription,
				shouldDisplayNavComponents: false
			};
		},
		fundObjective() {
			return this.getFundAttributes && this.getFundAttributes.Objective || '';
		},
		fundGroupDescription() {
			return this.getFundAttributes && this.getFundAttributes.FundGroupDescription;
		},
		investmentsLink() {
			return `${this.getBaseUrl}/${this.getAudience}/investments`;
		},
		fundNameSup() {
			return this.getFundAttributes && this.getFundAttributes.FundNameLegal || '';
		},
		quarterlyHoldingsData() {
			return this.getQuarterlyHoldingsData || {};
		},
		quarterlyHoldingsMetaData() {
			return this.getQuarterlyHoldingsMetaData || {};
		}
	},
	beforeMount() {
		this.$nextTick(() => {
			window.addEventListener('resize', () => {
				this.resizeBreakpoints();
			});
		});
	},
	methods: {
		changeSelectedType(type) {
			this.selectedType = type;
		},
		formatName(name, classNumber) {
			name = name.split(' ');
			const result = [];
			for (let i = 0; i < name.length; i++) {
				if (name[i] === '-') break;
				result.push(name[i].toLowerCase());
			}
			return `${result.join('-')}-${classNumber}`;
		},
		updateTable(data) {
			if (data.pagination) {
				this.setPaginationValue(data.pagination);
			}
			if (data.filterBy) {
				this.setFilter(data.filterBy);
			}
			this.sortBy = data.sortByLabel;
			store.dispatch('getAndCommit', {
				endpoint: 'quarterly-holdings',
				commitId: 'SET_QUARTERLY_HOLDINGS',
				stagingFlag: this.$store.state.stagingFlag,
				audience: this.getAudience,
				fundNumber: this.getFundAttributes.FundNumber,
				shareClass: this.getFundAttributes.ShareClass,
				sortBy: data.sortByLabel,
				sortDirection: data.sortDirection,
				firstRow: this.firstRow,
				assetType: this.filterKey
			});
		},
		setPaginationValue(pagination) {
			if (pagination) {
				switch (pagination) {
					case 'first':
						this.firstRow = 0;
						break;
					case 'prev':
						this.firstRow = this.firstRow === 0 ? 0 : this.firstRow - this.numberOfResultsRequested;
						break;
					case 'next':
						this.firstRow += this.numberOfResultsRequested;
						break;
					case 'last':
						this.firstRow = this.quarterlyHoldingsMetaData.totalFilterCount % this.numberOfResultsRequested !== 0 ? (this.quarterlyHoldingsMetaData.totalFilterCount - this.quarterlyHoldingsMetaData.totalFilterCount % this.numberOfResultsRequested) : this.quarterlyHoldingsMetaData.totalFilterCount - this.numberOfResultsRequested;
						break;
					default:
						break;
				}
			}
		},
		setFilter(filter) {
			switch (filter) {
				case 'Fixed-Income':
					this.filterKey = 'Fixed Income';
					break;
				case 'Cash & Equivalents':
					this.filterKey = 'Short Term';
					break;
				default:
					this.filterKey = filter;
					break;
			}
		}
	}
};
</script>
<style lang="less" scoped>
@import '../../../icomoon/icon-style.css';

/* CG:16562 this hides the download button*/
#quarterly-holdings a[href='#download-modal'] {
	display: none;
}

.holding-container {
	background-color: #f2f2f2;

	#holding-results {
		.flex-container {
			display: flex;
		}

		.shim-top-medium {
			margin-top: 25px;
		}

		background-color: white;
		padding: 0px 15px;
		max-width: 968px;
		margin: auto;

		.header {
			display: flex;
			display: -webkit-flex;
			padding: 20px 0px;
			margin-bottom: 15px;

			h2 {
				font-size: 18px;
				color: #333;
				font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
			}
		}

		.holdings-filters {
			.total-holdings {
				font-family: 'AvenirNextLTW01-Regular', Arial, sans-serif;
				color: #333;
				font-size: 14px;
			}

			#filter-dropdown {
				margin-top: 10px;
				display: inline-block;
				display: -webkit-inline-box;
			}

			.holdings-filters-list {
				display: inline-block;
				padding: 0;

				li {
					display: inline-block;
					margin-left: 10px;
					padding-right: 10px;
					font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;

					&:not(:last-of-type) {
						border-right: 2px solid #333;
					}

					a.active {
						color: #000000;
					}
				}
			}

			.as-of-date {
				font-size: 12px;
				font-family: 'AvenirNextLTW01-Condens 721299', Arial, sans-serif;
				color: #666;
				margin-top: 15px;
				margin-bottom: 30px;
			}
		}
	}

	@media (max-width: 768px) {
		#holding-results {
			.header {
				padding: 10px 0;
				padding-bottom: 0;
				margin-bottom: 10px;
				flex-direction: row-reverse;
				flex-direction: -webkit-row-reverse;
				flex-wrap: wrap;
				-webkit-flex-wrap: wrap;

				h2 {
					margin-bottom: 10px;
					margin-top: 40px;
					order: 1;
					-webkit-order: 1;
					margin-right: auto;
				}

				a {
					flex-basis: 100%;
					-webkit-flex-basis: 100%;
				}
			}

			.holdings-filters {
				#filter-dropdown {
					display: inline-block;
					display: webkit-inline-box;
				}
			}
		}
	}

	@media (max-width: 480px) {
		#holding-results {
			.holdings-filters {
				#filter-dropdown {
					display: block;
				}
			}
		}
	}
}
</style>